/**
 * Google Takeout importer modal.
 *
 * Three stages: upload → review → summary. The user drops files (CSV, KML,
 * JSON, or a full Takeout ZIP), we parse them entirely in the browser, show
 * a dedup-aware review table, and on confirm POST the selected rows to the
 * new /api/places/import endpoint in batches of 500.
 *
 * Privacy note: the raw Takeout data never leaves the device. We only POST
 * the minimum fields the user explicitly confirmed.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Upload, Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/Dialog'
import { Button } from '../ui/Button'
import { placeService, type Place } from '../../services/placeService'
import {
  aggregateTimelineVisits,
  candidateToPayload,
  dedupeAgainst,
  parseSingleFile,
  type ImportCandidate,
  type ZipImportDiagnostics,
} from '../../services/googlePlacesImport'
import { BRAND } from '../../config/brand'

type Stage = 'upload' | 'parsing' | 'review' | 'importing' | 'summary'

interface Props {
  open: boolean
  onClose: () => void
  /** Existing My Places list, used for dedup preview. */
  existingPlaces: Place[]
  /** Called after at least one place is imported — lets parent refresh + sync geofences. */
  onImported: () => void
}

const MAX_BYTES_PER_FILE = 50 * 1024 * 1024
const IMPORT_CHUNK_SIZE = 500

interface ParsedFileSummary {
  filename: string
  count: number
  error?: string
}

export const ImportGoogleModal: React.FC<Props> = ({
  open,
  onClose,
  existingPlaces,
  onImported,
}) => {
  const [stage, setStage] = useState<Stage>('upload')
  const [parseError, setParseError] = useState<string | null>(null)
  const [fileSummaries, setFileSummaries] = useState<ParsedFileSummary[]>([])
  const [candidates, setCandidates] = useState<ImportCandidate[]>([])
  const [selection, setSelection] = useState<Record<string, boolean>>({})
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 })
  const [summary, setSummary] = useState<{
    created: number
    skipped_dedup: number
    skipped_invalid: number
  } | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  // Lazy import to avoid pulling the candidate table into the upload stage.
  const [ImportCandidateTable, setImportCandidateTable] = useState<
    React.ComponentType<any> | null
  >(null)
  useEffect(() => {
    if (stage === 'review' && !ImportCandidateTable) {
      void import('./ImportCandidateTable').then((mod) => {
        setImportCandidateTable(() => mod.ImportCandidateTable)
      })
    }
  }, [stage, ImportCandidateTable])

  const reset = useCallback(() => {
    setStage('upload')
    setParseError(null)
    setFileSummaries([])
    setCandidates([])
    setSelection({})
    setImportProgress({ done: 0, total: 0 })
    setSummary(null)
  }, [])

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files)
      if (list.length === 0) return
      setStage('parsing')
      setParseError(null)
      const perFile: ParsedFileSummary[] = []
      const all: ImportCandidate[] = []
      let mergedZipDiag: ZipImportDiagnostics | undefined

      for (const file of list) {
        if (file.size > MAX_BYTES_PER_FILE) {
          perFile.push({
            filename: file.name,
            count: 0,
            error: `file is ${(file.size / (1024 * 1024)).toFixed(1)}MB — max 50MB. Split by month or trim and retry.`,
          })
          continue
        }
        try {
          const { candidates: parsed, source, zipDiagnostics } = await parseSingleFile(file)
          if (zipDiagnostics) {
            mergedZipDiag = mergedZipDiag
              ? {
                  unrecognized: [
                    ...mergedZipDiag.unrecognized,
                    ...zipDiagnostics.unrecognized,
                  ],
                  recognizedFiles: [
                    ...mergedZipDiag.recognizedFiles,
                    ...zipDiagnostics.recognizedFiles,
                  ],
                }
              : zipDiagnostics
          }
          if (source == null) {
            perFile.push({
              filename: file.name,
              count: 0,
              error: "unrecognized file — expected .csv, .kml, .json, or takeout .zip",
            })
            continue
          }
          all.push(...parsed)
          perFile.push({ filename: file.name, count: parsed.length })
        } catch (err) {
          perFile.push({
            filename: file.name,
            count: 0,
            error: err instanceof Error ? err.message : 'parse error',
          })
        }
      }

      // Collapse multi-visit timeline rows into one per place, then dedup
      // against the user's existing My Places.
      const aggregated = aggregateTimelineVisits(all)
      const deduped = dedupeAgainst(existingPlaces, aggregated)

      setFileSummaries(perFile)
      setCandidates(deduped)

      // Auto-select sensible defaults: non-duplicate, ready rows with either
      // (a) ≥2 visits / ≥30 min for timeline rows, or (b) anything else.
      const initial: Record<string, boolean> = {}
      for (const c of deduped) {
        if (c.dedupWith) {
          initial[c._key] = false
          continue
        }
        if (c.latitude == null || c.longitude == null) {
          initial[c._key] = false
          continue
        }
        const isTimeline =
          c.source === 'semantic_history' || c.source === 'timeline_json'
        if (isTimeline) {
          initial[c._key] =
            (c.visitCount ?? 0) >= 2 || (c.totalMinutes ?? 0) >= 30
        } else {
          initial[c._key] = true
        }
      }
      setSelection(initial)

      if (deduped.length === 0) {
        setStage('upload')
        const parts: string[] = ['No places found in the selected files.']
        if (mergedZipDiag) {
          const { recognizedFiles, unrecognized } = mergedZipDiag
          const withRows = recognizedFiles.filter((f) => f.count > 0).length
          const emptyOk = recognizedFiles.filter((f) => f.count === 0).length
          if (recognizedFiles.length > 0) {
            parts.push(
              `Inside the zip, ${withRows} file(s) contained parsed rows and ${emptyOk} matched a known type but had 0 places.`,
            )
          }
          if (unrecognized.length > 0) {
            const sample = unrecognized
              .slice(0, 4)
              .map((p) => p.split('/').pop() || p)
              .join(', ')
            parts.push(
              `${unrecognized.length} other file(s) were skipped (unsupported type). Examples: ${sample}.`,
            )
          }
        }
        parts.push(
          'In Takeout, enable Saved for per-list CSVs, and Maps or Maps (your places) for GeoJSON (e.g. Saved Places.json). Location History is a different category.',
        )
        setParseError(parts.join(' '))
      } else {
        setStage('review')
      }
    },
    [existingPlaces],
  )

  const selectedCandidates = useMemo(
    () => candidates.filter((c) => selection[c._key]),
    [candidates, selection],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragActive(false)
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        void handleFiles(e.dataTransfer.files)
      }
    },
    [handleFiles],
  )

  const handleRescue = useCallback(
    (key: string, lat: number, lng: number) => {
      const target = candidates.find((c) => c._key === key)
      if (!target) return
      // Patch in the rescued coords, then re-run dedup so a rescued candidate
      // that matches an existing My Place gets flagged instead of silently
      // creating a duplicate on the server.
      const [rededuped] = dedupeAgainst(existingPlaces, [
        { ...target, latitude: lat, longitude: lng },
      ])
      setCandidates((prev) =>
        prev.map((c) => (c._key === key ? rededuped : c)),
      )
      // Auto-select only if dedup didn't flag it as a duplicate.
      if (!rededuped.dedupWith) {
        setSelection((prev) => ({ ...prev, [key]: true }))
      }
    },
    [candidates, existingPlaces],
  )

  const doImport = useCallback(async () => {
    const payloads = selectedCandidates
      .map(candidateToPayload)
      .filter((p): p is NonNullable<typeof p> => p != null)
    if (payloads.length === 0) return
    setStage('importing')
    setImportProgress({ done: 0, total: payloads.length })
    let totalCreated = 0
    let dedupSkipped = 0
    let invalidSkipped = 0
    try {
      for (let i = 0; i < payloads.length; i += IMPORT_CHUNK_SIZE) {
        const chunk = payloads.slice(i, i + IMPORT_CHUNK_SIZE)
        const { created, skipped } = await placeService.batchImport(chunk)
        totalCreated += created.length
        for (const s of skipped) {
          if (s.reason === 'invalid') invalidSkipped += 1
          else dedupSkipped += 1
        }
        setImportProgress({ done: Math.min(i + chunk.length, payloads.length), total: payloads.length })
      }
      setSummary({
        created: totalCreated,
        skipped_dedup: dedupSkipped,
        skipped_invalid: invalidSkipped,
      })
      setStage('summary')
      if (totalCreated > 0) onImported()
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Import failed')
      setStage('review')
    }
  }, [selectedCandidates, onImported])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import places from Google</DialogTitle>
          <DialogDescription>
            Drop a Takeout .zip or individual files: Saved list CSVs, Maps
            GeoJSON (e.g. Saved Places.json), My Maps KML/KMZ, or Location
            History JSON. Parsed locally — only rows you confirm are sent to
            {BRAND.assistant}.
          </DialogDescription>
        </DialogHeader>

        {stage === 'upload' && (
          <div className="flex flex-col gap-3">
            <div
              className={`border-2 border-dashed rounded-md p-8 text-center transition-colors ${
                isDragActive
                  ? 'border-brand bg-brand-subtle'
                  : 'border-soft bg-surface-alt'
              }`}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragActive(true)
              }}
              onDragLeave={() => setIsDragActive(false)}
              onDrop={handleDrop}
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
              }}
            >
              <Upload className="w-8 h-8 text-muted mx-auto mb-2" />
              <div className="text-sm text-primary font-medium">
                Drop files here or click to browse
              </div>
              <div className="text-xs text-muted mt-1">
                Accepted: .zip, .csv, .kml, .kmz, .json, .geojson. Max 50MB per file.
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept=".zip,.csv,.kml,.kmz,.json,.geojson"
              onChange={(e) => {
                if (e.target.files) void handleFiles(e.target.files)
                e.target.value = ''
              }}
            />
            {parseError && (
              <div className="flex items-center gap-2 text-sm text-bad">
                <AlertTriangle className="w-4 h-4" />
                {parseError}
              </div>
            )}
            <HowToGetTakeout />
          </div>
        )}

        {stage === 'parsing' && (
          <div className="flex items-center justify-center py-8 gap-2 text-sm text-secondary">
            <Loader2 className="w-5 h-5 animate-spin" />
            Parsing files…
          </div>
        )}

        {stage === 'review' && (
          <div className="flex flex-col gap-3 min-h-0">
            {fileSummaries.length > 0 && (
              <FilesSummaryStrip summaries={fileSummaries} total={candidates.length} />
            )}

            {ImportCandidateTable ? (
              <ImportCandidateTable
                candidates={candidates}
                selection={selection}
                onSelectionChange={setSelection}
                onRescue={handleRescue}
              />
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading review table…
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-soft">
              <Button variant="outline" onClick={reset}>
                Start over
              </Button>
              <Button
                onClick={doImport}
                disabled={selectedCandidates.length === 0}
              >
                Import {selectedCandidates.length} place
                {selectedCandidates.length === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        )}

        {stage === 'importing' && (
          <div className="py-6 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-brand" />
            <div className="text-sm text-secondary">
              Importing {importProgress.done} / {importProgress.total}…
            </div>
            <div className="w-full bg-surface-alt rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-brand transition-all"
                style={{
                  width: `${importProgress.total > 0 ? (100 * importProgress.done) / importProgress.total : 0}%`,
                }}
              />
            </div>
          </div>
        )}

        {stage === 'summary' && summary && (
          <div className="flex flex-col gap-3 py-4">
            <div className="flex items-center gap-2 text-ok">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">Imported {summary.created} places</span>
            </div>
            {summary.skipped_dedup > 0 && (
              <div className="text-sm text-muted">
                Skipped {summary.skipped_dedup} duplicate
                {summary.skipped_dedup === 1 ? '' : 's'} (already in My Places).
              </div>
            )}
            {summary.skipped_invalid > 0 && (
              <div className="text-sm text-warn">
                Skipped {summary.skipped_invalid} invalid row
                {summary.skipped_invalid === 1 ? '' : 's'}.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-soft">
              <Button variant="outline" onClick={reset}>
                Import more
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

const FilesSummaryStrip: React.FC<{
  summaries: ParsedFileSummary[]
  total: number
}> = ({ summaries, total }) => (
  <div className="text-xs text-muted bg-surface-alt rounded-md p-2">
    <div className="flex items-center gap-1 mb-1">
      <Info className="w-3 h-3" />
      <span>{total} candidate place{total === 1 ? '' : 's'} parsed</span>
    </div>
    <div className="space-y-0.5">
      {summaries.map((s) => (
        <div key={s.filename} className="flex items-center gap-2">
          <span className="truncate flex-1" title={s.filename}>
            {s.filename}
          </span>
          {s.error ? (
            <span className="text-bad">{s.error}</span>
          ) : (
            <span>{s.count} rows</span>
          )}
        </div>
      ))}
    </div>
  </div>
)

const HowToGetTakeout: React.FC = () => (
  <details className="text-xs text-muted">
    <summary className="cursor-pointer select-none hover:text-secondary">
      How do I export from Google?
    </summary>
    <div className="mt-2 space-y-2 pl-2 border-l-2 border-soft">
      <div>
        <strong>Saved lists (Want to go, custom lists):</strong> At{' '}
        <a
          href="https://takeout.google.com"
          target="_blank"
          rel="noreferrer"
          className="text-brand underline"
        >
          takeout.google.com
        </a>
        , enable <em>Saved</em> — you get one CSV per list under a{' '}
        <code className="text-app-micro">Saved</code> folder.
      </div>
      <div>
        <strong>Starred / labeled pins (GeoJSON):</strong> In the same Takeout
        page, enable <em>Maps</em> and/or <em>Maps (your places)</em>. Upload
        the zip or files like <code className="text-app-micro">Saved Places.json</code>.
      </div>
      <div>
        <strong>My Maps:</strong> Takeout category <em>My Maps</em> — export
        includes KML/KMZ layers you can drop here.
      </div>
      <div>
        <strong>Timeline / places you've visited:</strong> Same Takeout flow
        but pick <em>Location History (Timeline)</em>. Upload the relevant
        month JSON files or the whole ZIP.
      </div>
      <div className="text-app-micro">
        New Android devices store Timeline on-device only — open Google Maps →
        Timeline → Settings → Export to get a JSON.
      </div>
    </div>
  </details>
)

export default ImportGoogleModal
